/**
 * The single source of truth for Asset Grade badge colours.
 *
 * Every badge that shows a letter grade reads from here: the spotlight card overall badge and
 * its five subscore badges, the history dropdown rows, and the full breakdown. Each letter has
 * its own fill and its own contrast text colour, so never substitute a shared colour per grade
 * family or a fixed text colour.
 *
 * These values match the palette already running in production. Treat them as locked.
 */
export interface GradeColor {
  color: string;
  textColor: string;
}

export const GRADE_NOT_AVAILABLE = 'N/A';

const GRADE_COLORS: Record<string, GradeColor> = {
  'A+': { color: '#009000', textColor: '#FFFFFF' },
  A: { color: '#32CD32', textColor: '#FFFFFF' },
  'A-': { color: '#50C878', textColor: '#000000' },
  'B+': { color: '#7ED321', textColor: '#000000' },
  B: { color: '#B8E600', textColor: '#000000' },
  'B-': { color: '#FFCC00', textColor: '#000000' },
  'C+': { color: '#FFB84D', textColor: '#000000' },
  C: { color: '#FFA500', textColor: '#FFFFFF' },
  'C-': { color: '#FF9500', textColor: '#FFFFFF' },
  'D+': { color: '#FA7467', textColor: '#000000' },
  D: { color: '#F05459', textColor: '#FFFFFF' },
  'D-': { color: '#E6344A', textColor: '#FFFFFF' },
  F: { color: '#FF0033', textColor: '#FFFFFF' },
  [GRADE_NOT_AVAILABLE]: { color: '#C0C0C0', textColor: '#000000' }
};

/**
 * Colour for a letter grade. Unknown, empty or missing grades fall back to the neutral N/A
 * treatment rather than rendering an uncoloured badge.
 */
export function gradeColor(grade: string | null | undefined): GradeColor {
  const key = (grade || '').trim().toUpperCase();
  return GRADE_COLORS[key] || GRADE_COLORS[GRADE_NOT_AVAILABLE];
}

/** The letter to print inside the badge, falling back to N/A when there is no grade. */
export function gradeLabel(grade: string | null | undefined): string {
  const key = (grade || '').trim().toUpperCase();
  return GRADE_COLORS[key] ? key : GRADE_NOT_AVAILABLE;
}
